/** Target first, then a durable intent, then a source tombstone (INV-4/5). */
import Dexie from "dexie";
import { db, familyDb } from "./db";
import type { LedgerRef } from "./ledger";
import type { LocalExpense } from "./types";
import { fromWireExpense } from "./types";
import { assertLedgerWritable, nextUpdatedAt, isLockedSplitShare, SplitExpenseLockedError } from "./repo";
import { queueSync } from "./sync";
import { parseFamilyView } from "./family";
import { isSavingsCategory } from "@/domain/category_kind";

const PREFIX = "pendingMove:";
type Owner = Pick<LedgerRef, "kind" | "ownerId">;
class InvalidMove extends Error {}
interface PendingMove { id: string; from: Owner; to: Owner; row: LocalExpense }
export interface MoveUndo { row: LocalExpense; from: LedgerRef; to: LedgerRef; destinationCategory?: string }
const store = (owner: Owner) => owner.kind === "personal" ? db : familyDb;
const dirty = { _dirty: 1 as const, _syncedAt: null };
let running: Promise<number> | null = null;
let activeMove: Promise<MoveUndo> | null = null;
let moveQueue: Promise<unknown> = Promise.resolve();

async function finish(marker: PendingMove): Promise<void> {
  const source = store(marker.from), target = store(marker.to);
  const sourceRow = await source.expenses.get(marker.id);
  if (sourceRow && sourceRow.userId !== marker.from.ownerId) throw new InvalidMove("Cannot recover expense move: source owner differs.");
  const existing = await target.expenses.get(marker.id);
  if (existing && existing.userId !== marker.to.ownerId) throw new InvalidMove("Cannot recover expense move: target owner differs.");
  // An interrupted marker may be replayed after a newer target edit. Preserve it.
  if (!existing || existing.updatedAt < marker.row.updatedAt) await target.expenses.put(marker.row);
  const row = await source.expenses.get(marker.id);
  if (row && row.userId !== marker.from.ownerId) throw new InvalidMove("Cannot recover expense move: source owner differs.");
  if (row && row.deletedAt === null) {
    const updatedAt = nextUpdatedAt(row.updatedAt);
    await source.expenses.put({ ...row, deletedAt: updatedAt, updatedAt, ...dirty });
  }
  const settled = await target.expenses.get(marker.id);
  if (settled?._moveFrom) { delete settled._moveFrom; await target.expenses.put(settled); }
  await source.meta.delete(`${PREFIX}${marker.id}`);
  for (const database of [db, familyDb]) await database.meta.delete(`quarantinedMove:${marker.id}`);
}
function parseMarker(value: string): PendingMove {
  const raw: unknown = JSON.parse(value);
  if (typeof raw !== "object" || raw === null) throw new InvalidMove("Cannot recover expense move: invalid marker.");
  const marker = raw as Record<string, unknown>;
  const owner = (value: unknown): Owner => {
    if (typeof value !== "object" || value === null) throw new InvalidMove("Cannot recover expense move: invalid ledger.");
    const ref = value as Record<string, unknown>;
    if ((ref.kind !== "personal" && ref.kind !== "family") || typeof ref.ownerId !== "string") throw new InvalidMove("Cannot recover expense move: invalid ledger.");
    return { kind: ref.kind, ownerId: ref.ownerId };
  };
  const from = owner(marker.from), to = owner(marker.to);
  if (from.kind === to.kind || typeof marker.id !== "string") throw new InvalidMove("Cannot recover expense move: invalid destination.");
  const row = fromWireExpense(marker.row);
  if (row.id !== marker.id || row.userId !== to.ownerId || row.deletedAt !== null) throw new InvalidMove("Cannot recover expense move: invalid target row.");
  const attribution = typeof marker.row === "object" && marker.row !== null ? marker.row as Record<string, unknown> : {};
  return { id: marker.id, from, to, row: { ...row, ...dirty,
    ...(typeof attribution._extra === "object" && attribution._extra !== null ? { _extra: attribution._extra as Record<string, unknown> } : {}),
    ...(typeof attribution._createdBy === "string" ? { _createdBy: attribution._createdBy } : {}),
    ...(typeof attribution._updatedBy === "string" ? { _updatedBy: attribution._updatedBy } : {}),
  } };
}
export async function recoverExpenseMoves(): Promise<number> {
  if (activeMove) { try { await activeMove; } catch (cause) { console.warn("Active expense move failed:", cause); } }
  if (running) return running;
  running = (async () => {
    // Do not create a family database for an account that has never had one.
    if (!await db.meta.get("family:id") && !familyDb.isOpen()) return 0;
    if (!familyDb.isOpen() && !await Dexie.exists(familyDb.name)) return 0;
    const unresolved = new Set<string>();
    const quarantine = async (id: string, value: string) => {
      await db.meta.put({ key: `quarantinedMove:${id}`, value });
      await familyDb.meta.delete(`quarantinedMove:${id}`);
    };
    for (const source of [db, familyDb]) {
      const markers = await source.meta.where("key").startsWith(PREFIX).toArray();
      for (const marker of markers) {
        const id = marker.key.slice(PREFIX.length);
        let parsed: PendingMove;
        try { parsed = parseMarker(marker.value); if (parsed.id !== id) throw new InvalidMove("Cannot recover expense move: marker id differs."); }
        catch (cause) {
          console.warn("Expense move marker is unreadable:", cause);
          unresolved.add(id);
          try { await quarantine(id, marker.value); await source.meta.delete(marker.key); }
          catch (cause) { console.warn("Could not quarantine expense move:", cause); }
          continue;
        }
        try { await finish(parsed); unresolved.delete(id); }
        catch (cause) {
          console.warn("Expense move recovery failed:", cause); unresolved.add(id);
          if (cause instanceof InvalidMove) {
            try { await quarantine(id, marker.value); await source.meta.delete(marker.key); }
            catch (cause) { console.warn("Could not quarantine expense move:", cause); }
          }
        }
      }
    }
    for (const target of [db, familyDb]) {
      const rows = await target.expenses.where("_moveFrom.kind").anyOf("personal", "family").toArray();
      for (const row of rows) {
        if (!row._moveFrom) continue;
        try {
          await finish({ id: row.id, from: row._moveFrom, to: { kind: target === db ? "personal" : "family", ownerId: row.userId }, row });
          unresolved.delete(row.id);
        } catch (cause) {
          console.warn("Expense move target recovery failed:", cause); unresolved.add(row.id);
          if (cause instanceof InvalidMove) {
            try { await quarantine(row.id, JSON.stringify(row)); const clean = { ...row }; delete clean._moveFrom; await target.expenses.put(clean); }
            catch (cause) { console.warn("Could not quarantine expense move target:", cause); }
          }
        }
      }
      for (const record of await target.meta.where("key").startsWith("quarantinedMove:").toArray()) unresolved.add(record.key.slice("quarantinedMove:".length));
    }
    return unresolved.size;
  })();
  try { return await running; } catch (cause) { console.warn("Expense move recovery unavailable:", cause); return 1; } finally { running = null; }
}
/** Discard only stale recovery intents; keep every expense row. */
export async function dismissExpenseMoveFailures(): Promise<void> {
  for (const database of [db, familyDb]) {
    const records = await database.meta.where("key").startsWith("quarantinedMove:").toArray();
    for (const record of records) {
      const id = record.key.slice("quarantinedMove:".length);
      for (const store of [db, familyDb]) {
        await store.meta.delete(`${PREFIX}${id}`);
        const row = await store.expenses.get(id);
        if (row?._moveFrom) { delete row._moveFrom; await store.expenses.put(row); }
        await store.meta.delete(`quarantinedMove:${id}`);
      }
    }
  }
}
async function performMove(id: string, patch: Partial<Pick<LocalExpense, "categoryId" | "amountCents" | "occurredAt" | "note">>, from: LedgerRef, to: LedgerRef, restoring?: LocalExpense): Promise<MoveUndo> {
  assertLedgerWritable(from);
  assertLedgerWritable(to);
  if (from.kind === to.kind) throw new Error("Choose a category in the other ledger.");
  const row = await from.db.expenses.get(id);
  if (!row || row.deletedAt !== null || row.userId !== from.ownerId) throw new Error("That expense is no longer available.");
  if (await isLockedSplitShare(row, from)) throw new SplitExpenseLockedError(row);
  const category = await to.db.categories.get(patch.categoryId ?? row.categoryId);
  if (!category || category.userId !== to.ownerId || category.deletedAt !== null || (category.archivedAt !== null && !restoring) || (isSavingsCategory(category) && !restoring)) throw new Error("Choose a live expenditure category in the destination ledger.");
  const own = await from.db.categories.get(row.categoryId);
  if (to.kind === "family" && own && isSavingsCategory(own)) throw new Error("Savings expenses cannot move to Family.");
  const previous = await to.db.expenses.get(id);
  const updatedAt = nextUpdatedAt(previous && previous.updatedAt > row.updatedAt ? previous.updatedAt : row.updatedAt);
  const target: LocalExpense = { ...row, ...restoring, _moveFrom: { kind: from.kind, ownerId: from.ownerId }, ...patch, userId: to.ownerId, updatedAt, deletedAt: null, recurringRuleId: restoring?.recurringRuleId ?? null, sourceId: restoring?.sourceId ?? null, ...dirty };
  delete target._createdBy; delete target._updatedBy;
  if (to.kind === "family") {
    delete target.splitRef;
    const cache = await db.meta.get("family:view");
    if (cache) {
      const family = parseFamilyView(JSON.parse(cache.value));
      if (family.family_id !== to.ownerId) throw new Error("Family membership does not match this ledger.");
      target._createdBy = restoring?._createdBy ?? previous?._createdBy ?? family.my_member_id;
      target._updatedBy = family.my_member_id;
    }
  }
  const marker: PendingMove = { id, from: { kind: from.kind, ownerId: from.ownerId }, to: { kind: to.kind, ownerId: to.ownerId }, row: target };
  await to.db.expenses.put(target);
  await from.db.meta.put({ key: `${PREFIX}${id}`, value: JSON.stringify(marker) });
  await finish(marker);
  queueSync();
  return { row, from, to, destinationCategory: category.name };
}
function enqueueMove(work: () => Promise<MoveUndo>): Promise<MoveUndo> {
  const result = moveQueue.then(async () => { await recoverExpenseMoves(); activeMove = work(); try { return await activeMove; } finally { activeMove = null; } });
  moveQueue = result.catch(cause => { console.warn("Expense move failed:", cause); });
  return result;
}
export async function moveExpense(id: string, patch: Parameters<typeof performMove>[1], from: LedgerRef, to: LedgerRef): Promise<MoveUndo> {
  return enqueueMove(() => performMove(id, patch, from, to));
}
export async function undoExpenseMove(undo: MoveUndo): Promise<void> {
  await enqueueMove(() => performMove(undo.row.id, { categoryId: undo.row.categoryId, amountCents: undo.row.amountCents, note: undo.row.note, occurredAt: undo.row.occurredAt }, undo.to, undo.from, undo.row));
}
